import { Skeleton, Stack, StackProps, styled } from "@mui/material";
import { memo, useEffect, useMemo, useState, Fragment } from "react";
import { FarmCardType } from "types/farm-info";
import useGetData from "hooks/useGetData";
import { apiGetCompanyDetail } from "services/api/company";
import FreeBuyerContact from "./FreeBuyerContact";
import PaidBuyerContact from "./PaidBuyerContact";
import { BuyerSkeleton } from "Components/skeleton";
import { colorPremiumFarm, isCheckCardFarm, isPremiumBuyer } from "helpers/plan";

type Props = {
  contact_id?: string;
  buyerDetail?: FarmCardType;
  fullLink?: boolean;
  isBiz?: boolean;
  onBonus?: () => void;
  disabledLink?: boolean;
  supplierId?: string;
  is_expired?: boolean;
};

const FarmContact = ({
  contact_id,
  buyerDetail,
  fullLink,
  isBiz = false,
  onBonus,
  disabledLink,
  supplierId,
  is_expired = false,
  ...rest
}: StackProps & Props) => {
  const [showInfors, setShowInfors] = useState(false);
  const [reloadContact, setReloadContact] = useState(false);
  const { data, isLoading } = useGetData({
    promise: () => (supplierId ? apiGetCompanyDetail(supplierId) : null),
    deps: [supplierId],
  });

  const buyerCardDetail: FarmCardType = useMemo(
    () => data?.data?.farm || buyerDetail || {},
    [data, buyerDetail]
  );

  const { is_owned_contact, is_featured, plan } = buyerCardDetail;

  const isFeatured = isPremiumBuyer(is_featured);
  const isCheckLevelCard = useMemo(() => plan && isCheckCardFarm(plan), [plan])
  const backgroundPremimum = useMemo(() => colorPremiumFarm(isCheckLevelCard), [isCheckLevelCard])

  const onBonusGetContact = () => {
    setReloadContact(!reloadContact);
    setShowInfors(true);
    onBonus && onBonus();
  };

  useEffect(() => {
    if (is_owned_contact) {
      setShowInfors(true);
    }
  }, [is_owned_contact]);

  return (
    <StyledBuyerContact
      spacing={{ xs: 1, md: isFeatured ? 1 : 2 }}
      sx={{
        background: buyerCardDetail && Object.keys(buyerCardDetail).length > 0 ? backgroundPremimum : "#fff",
        p: isFeatured ? { xs: 1, md: 1 } : { xs: 1, md: 2 },
      }}
      borderRadius={'12px'}
      padding={'8px !important'}
      {...rest}
    >
      {isLoading && (
        <Fragment>
          <Skeleton width={164} height={20} />
          <BuyerSkeleton />
        </Fragment>
      )}
      {!isLoading && (
        <Fragment>
          {(isCheckLevelCard === 0) ? (
            <FreeBuyerContact
              {...{
                contact_id,
                buyerCardDetail,
                buyerDetail,
                onBonusGetContact,
                fullLink,
                disabledLink,
                isBiz,
                showInfors,
                is_expired,
              }}
            />
          ) : (
            <PaidBuyerContact
              {...{
                contact_id,
                buyerCardDetail,
                buyerDetail,
                onBonusGetContact,
                fullLink,
                disabledLink,
                isBiz,
                showInfors,
                is_expired
              }}
            />
          )}
        </Fragment>
      )}
    </StyledBuyerContact>
  );
};

const StyledBuyerContact = styled(Stack)({
  ".form": {
    position: "relative",
  },
  fontSize: 16,
  ".get_card_button": {
    "&.Mui-disabled": {
      opacity: 0.6,
    },
  },
  ".contact-btn": {
    ".cost": {},
  },
  ".become-pre-btn": {
    a: {
      textDecorationColor: "var(--yellow-500)",
    },
    span: {
      color: "var(--yellow-500)",
    },
  },
});

export default memo(FarmContact);
